/**
 * Reading `cost_method`, which is free text and cannot be trusted to be prose.
 *
 * The column holds whatever a representative types into the "how that was
 * worked out" textarea. One seeded claim held a JSON object instead — headline,
 * confidence, method and an array of input rows — and the workbench printed it
 * to the page verbatim: braces, quoted keys, an escaped em dash, all of it
 * under a heading a teacher reads as the group's costing.
 *
 * Fixing that row fixed that row. This is here because the column is free text
 * and nothing stops the next import, paste or seed script from putting a
 * structure back into it, and because the failure is silent — no error, no
 * empty state, just machine punctuation in the middle of an argument.
 *
 * Split out of the component rather than written inside it so it can be tested.
 * There is no DOM harness in this app and every other test here is over a pure
 * function, so a parser that lives in JSX is a parser with no tests.
 *
 * Three outcomes, and the third is the one that matters:
 *
 *   - prose        — the ordinary case, shown as written.
 *   - structured   — a recognised shape, unpacked into its sentence and its
 *                    figures, so the reasoning survives instead of being hidden.
 *   - null         — JSON we cannot read. Nothing is shown, deliberately. An
 *                    unreadable costing is worth less than an honest blank: a
 *                    rep who finds this empty fills it in, where one who finds
 *                    it full of syntax may read it aloud in a meeting.
 */

export type CostMethodView =
  | { kind: 'prose'; text: string }
  | { kind: 'structured'; method: string | null; inputs: [string, string][] }
  | null;

export function readCostMethod(raw: string | null | undefined): CostMethodView {
  if (raw === null || raw === undefined) return null;

  const trimmed = raw.trim();
  if (trimmed === '') return null;

  // Only text that announces itself as a structure is parsed. Prose starting
  // with a brace is not a thing anyone writes, and attempting JSON.parse on
  // every value would be a lot of exceptions thrown in the common case.
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
    return { kind: 'prose', text: trimmed };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    // Starts like JSON, is not JSON. Showing it would print the braces.
    return null;
  }

  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null;

  const shape = parsed as { method?: unknown; inputs?: unknown };
  const method = typeof shape.method === 'string' && shape.method.trim() !== ''
    ? shape.method.trim()
    : null;

  const inputs: [string, string][] = Array.isArray(shape.inputs)
    ? shape.inputs.filter(
        (row): row is [string, string] =>
          Array.isArray(row) &&
          row.length === 2 &&
          typeof row[0] === 'string' &&
          typeof row[1] === 'string',
      )
    : [];

  // A recognised container with nothing readable in it is still nothing
  // readable. Falling through to prose here would print the braces, which is
  // the whole defect.
  if (method === null && inputs.length === 0) return null;

  return { kind: 'structured', method, inputs };
}
